import {
  Typography,
  Card,
  CardContent,
  TableContainer,
  Paper,
  Table,
  useTheme,
  TableBody,
  Grid,
  Link,
  Theme,
  SxProps,
} from '@mui/material';
import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { ToastbarContext } from '../../App';
import { useLoading } from '../../hooks/useLoading';
import { Toast } from '../../models/Toast';
import Spacer from '../Spacer';
import { DataTableDeleteModal } from './DataTableDeleteModal';
import {
  DataTableHeader,
  LastColumnElement,
  ModalText,
  RowData,
  TableType,
  AlignedElement,
} from './types';
import { TableHeader } from './TableHeader';
import { ActivePaymentMethodData } from '../../api/apis/AdminPaymentApi';
import { DataTableRow } from './DataTableRow/DataTableRow';
import { sortByDate, sortDollars } from '../../helpers/sortUtils';
import { isDate, isDollar } from '../../helpers/utilityFunctions';
import { PopupMenuItem as EllipsesItem } from '../PopupMenu';
import { SortConfig } from './types';

export const CELL_HORIZONTAL_PADDING = '5px';
export const LEADING_COLUMN_WIDTH = '80px';
export const ELLIPSIS_COLUMN_WIDTH = '48px';

export interface DataTableProps {
  title?: string;
  headers: DataTableHeader[];
  leftElement?: ReactNode;
  rightElement?: ReactNode | AlignedElement;
  backgroundColor?: string;
  outlined?: boolean;
  hideCaret?: boolean;
  scrollable?: boolean;
  data: RowData[];
  type?: TableType;
  lastColumnElement?: LastColumnElement | ((row: any) => LastColumnElement);
  lastColumnStyles?: React.CSSProperties;
  rowStyles?: SxProps<Theme>;
  borderOverride?: SxProps<Theme>;
  innerTableSx?: SxProps<Theme>;
  button1Text?: string;
  onButton1Click?: (row: any) => void;
  button1Disabled?: boolean;
  onDelete?: (row: any) => Promise<void>;
  emptyStateText?: string;
  filtered?: boolean;
  ellipsesItems?: EllipsesItem[];
  itemsPerPage?: number;
  customSort?: {
    column: string;
    function: (a: any, b: any) => number;
  }[];
  loading?: boolean;
  expandedContent?: (row: RowData) => ReactNode;
  isNested?: boolean;
  sortConfig?: SortConfig;
  setSortConfig?: Dispatch<SetStateAction<SortConfig>>;
}

const DataTable = ({
  title,
  headers,
  leftElement,
  rightElement,
  backgroundColor,
  outlined,
  hideCaret,
  scrollable,
  data,
  type,
  lastColumnElement = 'none',
  lastColumnStyles,
  rowStyles,
  borderOverride,
  innerTableSx,
  ellipsesItems,
  button1Text,
  onButton1Click,
  button1Disabled,
  onDelete,
  emptyStateText,
  filtered,
  itemsPerPage,
  customSort,
  loading,
  expandedContent,
  isNested = false,
  sortConfig: sortConfigProp,
  setSortConfig: setSortConfigProp,
}: DataTableProps) => {
  const theme = useTheme();
  const { setToast } = useContext(ToastbarContext);
  const { setLoading } = useLoading();
  const [localSort, setLocalSort] = useState<SortConfig>({ column: '', direction: 'asc' });
  const sortConfig = sortConfigProp ?? localSort;
  const setSortConfig = setSortConfigProp ?? setLocalSort;

  const [sortedData, setSortedData] = useState<RowData[]>(data);
  const [isInitialSort, setIsInitialSort] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeData, setActiveData] = useState<any>();
  const [visibleCount, setVisibleCount] = useState(itemsPerPage || data.length);
  const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(new Set());

  const cellVariant = outlined ? 'outlined' : undefined;
  const hasLeadingColumn =
    Boolean(expandedContent) || isNested || data.some(row => row.deletableRow);
  const showLoadMore = itemsPerPage && data.length > visibleCount;
  const flexLayout = headers.some(h => h.flex !== undefined);

  const modalText: ModalText | undefined =
    activeData && type === 'paymentMethod'
      ? {
          title: 'Remove payment method?',
          primaryText: `Are you sure you want to remove ${
            (activeData as ActivePaymentMethodData).name
          } as a payment method?`,
          secondaryText: 'This action cannot be undone.',
          toastMessage: `${(activeData as ActivePaymentMethodData).name} removed`,
        }
      : undefined;

  useEffect(() => {
    setLoading(loading ?? false);
    return () => setLoading(false);
  }, [loading, setLoading]);

  const toggleRowExpansion = (id: string) => {
    setExpandedRowIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const extractSortableVal = (value: any): any => {
    if (React.isValidElement(value)) {
      const props = value.props as any;

      if (props.value !== undefined) {
        return props.value;
      }
      if (props.text !== undefined) {
        return props.text;
      }
      if (props.children !== undefined) {
        return extractSortableVal(props.children);
      }
    }

    if (Array.isArray(value)) {
      return value
        .map(item => extractSortableVal(item))
        .filter(val => val !== null && val !== undefined)
        .join('');
    }

    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return value;
    }
    return String(value || '');
  };

  useEffect(() => {
    if (!data?.length) {
      setSortedData([]);
      return;
    }

    let sorted = [...data];
    const customSortFn = customSort?.find(sort => sort.column === sortConfig.column)?.function;

    const isAscending = sortConfig.direction === 'asc';

    if (customSortFn) {
      sorted.sort((a, b) => (isAscending ? -customSortFn(a, b) : customSortFn(a, b)));
    } else {
      sorted.sort((a: any, b: any) => {
        const aVal = extractSortableVal(a[sortConfig.column]);
        const bVal = extractSortableVal(b[sortConfig.column]);

        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return isAscending ? 1 : -1;
        if (bVal == null) return isAscending ? -1 : 1;
        if (aVal === bVal) return 0;

        if (isDate(aVal) && isDate(bVal)) {
          const comparison = sortByDate(aVal, bVal);
          return isAscending ? comparison : -comparison;
        }

        if (isDollar(aVal) && isDollar(bVal)) {
          const comparison = sortDollars(aVal, bVal);
          return isAscending ? comparison : -comparison;
        }

        const aStr = String(aVal).toLowerCase();
        const bStr = String(bVal).toLowerCase();

        return isAscending ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
      });
    }

    if (isInitialSort) {
      const pinnedItems = sorted.filter(item => item.isPinned);
      const unpinnedItems = sorted.filter(item => !item.isPinned);
      sorted = [...pinnedItems, ...unpinnedItems];
    }

    setSortedData(visibleCount > 0 ? sorted.slice(0, visibleCount) : sorted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, sortConfig, visibleCount, customSort, isInitialSort]);

  const onDeleteClick = (row: any) => {
    if (type !== undefined) {
      setActiveData(row);
      setModalOpen(true);
      return;
    }
    deleteRow(row);
  };

  const deleteRow = async (row: any) => {
    if (onDelete) {
      await onDelete(row);
    } else {
      setSortedData(prev => prev.filter(item => item !== row));
    }
  };

  const onHandleSave = () => {
    deleteRow(activeData);
    setToast(new Toast({ message: modalText?.toastMessage, severity: 'success', open: true }));
  };

  const onLoadMore = () => {
    setVisibleCount(prev => prev + (itemsPerPage || data.length));
  };

  const handleHeaderSort = (header: { label: string }) => {
    setIsInitialSort(false);
    setSortConfig(prev => {
      if (prev.column === header.label) {
        return { column: header.label, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { column: header.label, direction: 'asc' };
    });
  };

  if (!sortedData?.length) {
    return (
      <>
        {title && (
          <>
            <Typography variant='subtitle1' color='secondary.main'>
              {title}
            </Typography>
            <Spacer height='xxs' />
          </>
        )}
        <Card variant='ghost' color={backgroundColor ?? 'primary'}>
          <CardContent sx={{ textAlign: 'center' }} className='centered-content'>
            <Spacer height='xxs' />
            <Typography variant='h3' color='secondary.main'>
              {filtered ? 'No results found' : emptyStateText}
            </Typography>
            <Spacer height='xxs' />
          </CardContent>
        </Card>
        <Spacer height='lg' />
      </>
    );
  }

  const tableContent = (
    <>
      <TableContainer className={scrollable ? 'scrollable' : undefined} component={Paper}>
        <Table
          sx={[{ minWidth: 650 }, ...(Array.isArray(innerTableSx) ? innerTableSx : [innerTableSx])]}
          aria-label='data table'>
          <TableHeader
            headers={headers}
            leftElement={leftElement}
            rightElement={rightElement}
            sortByCol={sortConfig.column}
            sortDirection={sortConfig.direction}
            hideCaret={hideCaret}
            outlined={outlined}
            hasLeadingColumn={hasLeadingColumn}
            lastColumnElement={lastColumnElement}
            flexLayout={flexLayout}
            borderOverride={borderOverride}
            isNested={isNested}
            onSort={handleHeaderSort}
          />

          <TableBody>
            {sortedData.map((row, index) => (
              <DataTableRow
                key={String(row.id ?? index)}
                headers={headers}
                row={row}
                index={index}
                cellVariant={cellVariant}
                type={type}
                rowStyles={rowStyles}
                lastColumnStyles={lastColumnStyles}
                borderOverride={borderOverride}
                onDeleteClick={onDeleteClick}
                onButton1Click={onButton1Click}
                button1Text={button1Text}
                button1Disabled={button1Disabled}
                hasLeadingColumn={hasLeadingColumn}
                flexLayout={flexLayout}
                lastColumnElement={lastColumnElement}
                ellipsesItems={ellipsesItems}
                isNested={isNested}
                isExpanded={expandedRowIds.has(String(row.id))}
                onExpand={() => toggleRowExpansion(String(row.id))}
                expandedContent={expandedContent?.(row)}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {showLoadMore && (
        <Grid container justifyContent='center' mt={1} mb={1}>
          <Link
            onClick={onLoadMore}
            variant='p14'
            color='secondary'
            sx={{ textDecorationColor: theme.palette.secondary.main }}>
            Load More
          </Link>
        </Grid>
      )}
    </>
  );

  return (
    <>
      {type && modalText && (
        <DataTableDeleteModal
          open={modalOpen}
          setOpen={setModalOpen}
          modalText={modalText}
          onSave={onHandleSave}
          activeData={activeData}
        />
      )}
      {title && (
        <>
          <Typography variant='subtitle1' color='secondary.main'>
            {title}
          </Typography>
          <Spacer height='xxs' />
        </>
      )}

      <Card variant='ghost' color={isNested ? 'transparent' : (backgroundColor ?? 'primary')}>
        {!isNested || leftElement || rightElement ? (
          <CardContent className={hasLeadingColumn ? 'leadingColumn' : 'centered-content'}>
            {tableContent}
          </CardContent>
        ) : (
          tableContent
        )}
      </Card>
    </>
  );
};

export default DataTable;
